import React from 'react';
import { motion } from 'framer-motion';
import logo from '/img/procesta.png';
import muscleReport from '/img/pain-1.jpg';
import postureReport from '/img/pain-2.jpg';
import bodyImage from '/img/body.webp';
import MedicalServicesSection from "../components/MedicalServicesSection";
const HomePage = () => {
	const conditions = [
		{
		  title: 'Bodily stiffness and inflexibility',
		  desc: 'Neck and back stiffness, frozen shoulder, carpal tunnel syndrome, joint inflexibility.',
		},
		{
		  title: 'Muscle soreness and sensitivity',
		  desc: 'Physical stress, work-related fatigue and weakness, delayed muscle onset soreness (DMOS), muscle inflammation, tenderness and sensitivity to touch.',
		},
		{
		  title: 'Chronic back pain and spine-related symptoms',
		  desc: 'Acute back pain, lumbar stenosis, multiple scoliosis, spinal curvature disorders, and general inflexibility due to pre-existing conditions.',
		},
		{
		  title: 'Sports and fitness injuries',
		  desc: 'Swollen muscles, knee pain, Achilles tendon injuries, rotatory cuff injuries, dislocation therapy, head trauma, general injury recovery.',
		},
		{
		  title: 'Pregnancy-related afflictions',
		  desc: 'Spinal nerve stress, spinal misalignment, pelvic girdle pain, pelvic misalignment, increased back curvature, pregnancy-related stress.',
		},
		{
		  title: 'Illnesses arising from old age',
		  desc: 'Severe arthritis, weakness, in-ear balance, decreased mobility, joint degeneration, osteoporosis, muscle numbness, hip fractures, hernia.',
		},
	];
	function InfoCard({ title, text }) {
	  return (
		<div className="flex gap-3 items-start bg-white/4 p-4 rounded-xl ring-1 ring-white/6">
		  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/8">
			<svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden>
			  <path d="M12 2v20" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
			</svg>
		  </div>
		  <div>
			<p className="font-semibold text-white">{title}</p>
			<p className="text-sm text-white/80">{text}</p>
		  </div>
		</div>
	  );
	}

	function Badge() {
	  return (
		<div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/8 grid place-items-center">
		  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
			<path d="M20 6L9 17l-5-5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
		  </svg>
		</div>
	  );
	}
  return (
	<>
    <section className="relative overflow-hidden">
      {/* Background gradient that matches a vibrant 'logo' aesthetic */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06b6d4] via-[#3b82f6] to-[#2563eb] opacity-95 -z-10" />

      {/* Decorative animated SVG - waves + floating circles */}
      <svg
        className="absolute left-0 top-0 w-full h-full pointer-events-none"
        viewBox="0 0 1440 600"
        preserveAspectRatio="none"
        aria-hidden
      >
        <defs>
          <linearGradient id="g1" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0.02" />
          </linearGradient>
          <linearGradient id="g2" x1="0" x2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Soft glass overlay */}
        <rect width="1440" height="600" fill="url(#g1)" />

        {/* Animated wave path */}
        <g className="opacity-40" transform="translate(0,380)">
          <path
            d="M0,120 C180,200 360,60 540,120 C720,180 900,80 1080,120 C1260,160 1440,100 1440,100 L1440,600 L0,600 Z"
            fill="#ffffff"
            style={{ mixBlendMode: "soft-light" }}
          >
            <animate
              attributeName="d"
              dur="12s"
              repeatCount="indefinite"
              values="M0,120 C180,200 360,60 540,120 C720,180 900,80 1080,120 C1260,160 1440,100 1440,100 L1440,600 L0,600 Z;
                      M0,140 C180,120 360,200 540,140 C720,80 900,160 1080,120 C1260,60 1440,140 1440,140 L1440,600 L0,600 Z;
                      M0,120 C180,200 360,60 540,120 C720,180 900,80 1080,120 C1260,160 1440,100 1440,100 L1440,600 L0,600 Z"
            />
          </path>
        </g>

        {/* Floating circles */}
        <circle cx="110" cy="90" r="60" fill="#fff" opacity="0.06">
          <animate attributeName="cy" dur="8s" repeatCount="indefinite" values="70;110;70" />
        </circle>
        <circle cx="1300" cy="140" r="40" fill="#fff" opacity="0.04">
          <animate attributeName="cy" dur="10s" repeatCount="indefinite" values="120;160;120" />
        </circle>
      </svg>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          {/* Left: Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-white"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              {/* Logo placeholder: a simple circle + cross mark matching gradient */}
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-white/10 ring-1 ring-white/20">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M12 3v18M3 12h18" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium opacity-90">Procesta</p>
                <p className="text-xs opacity-80">Physiotherapy Hospital</p>
              </div>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-4">
              WELCOME TO <span className="bg-clip-text text-transparent bg-gradient-to-r from-white/95 via-white/80 to-white/60">PROCESTA PHYSIOTHERAPY HOSPITAL</span>
            </h1>

            <p className="max-w-xl text-white/90 text-base sm:text-lg mb-6">
              Where your health and well-being are our top priorities
            </p>

            <div className="flex flex-wrap gap-4 items-center mb-8">
              <a
                href="#learn-more"
                className="inline-flex items-center gap-3 px-5 py-3 rounded-2xl bg-white text-slate-900 font-semibold shadow-lg hover:scale-[1.02] transition-transform"
                aria-label="Learn more about Procesta"
              >
                Learn More
              </a>

              <a
                href="#appointment"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/20 text-white/95 hover:bg-white/10 transition"
              >
                Make Appointment
              </a>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <InfoCard title="Compassion" text="We treat every patient with kindness, empathy, and respect." />
              <InfoCard title="Excellence" text="We are committed to achieving the highest standards in medical care and patient safety." />
              <InfoCard title="Integrity" text="We uphold the highest ethical standards in all our practices." />
            </div>
          </motion.div>

          {/* Right: Visual panel (card with additional text + subtle image placeholder) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.12 }}
            className="relative"
          >
            <div className="rounded-2xl bg-white/6 backdrop-blur-sm p-6 md:p-8 ring-1 ring-white/10 shadow-2xl">
              <h3 className="text-white font-bold text-xl mb-3">Why Choose Procesta Physiotherapy Hospital?</h3>
              <p className="text-white/90 mb-4 max-w-md">
                We envision a healthier future for all, where advanced medical treatments and compassionate care are accessible to everyone. We
                strive to be a leader in healthcare innovation, education, and research, ensuring that our patients receive the best possible care.
              </p>

              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Badge />
                  <span className="text-white/90">Advanced personalized treatment plans</span>
                </li>
                <li className="flex items-start gap-3">
                  <Badge />
                  <span className="text-white/90">Experienced & caring clinicians</span>
                </li>
                <li className="flex items-start gap-3">
                  <Badge />
                  <span className="text-white/90">State-of-the-art facilities</span>
                </li>
              </ul>

              <div className="mt-6">
                <a href="#services" className="inline-block text-sm font-medium underline text-white/90">
                  Learn more about our services →
                </a>
              </div>
            </div>

            {/* subtle floating card accent */}
            <div className="absolute -top-8 right-6 w-32 h-32 rounded-lg bg-white/4 ring-1 ring-white/10 flex items-center justify-center text-white font-semibold text-xs">
              24/7
              <span className="block text-[10px] opacity-80">Care Support</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Decorative bottom wave to visually anchor the hero */}
      <svg className="w-full" viewBox="0 0 1440 120" preserveAspectRatio="none" aria-hidden>
        <path d="M0,80 C360,0 1080,160 1440,80 L1440,120 L0,120 Z" fill="#ffffff" opacity="0.06" />
      </svg>
    </section>
	
    <section className="relative bg-gradient-to-b from-blue-50 to-white py-24 overflow-hidden">
      {/* Background SVG */}
      <div className="absolute top-0 left-0 w-full">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          className="w-full h-32 text-blue-200"
        >
          <path
            fill="currentColor"
            fillOpacity="1"
            d="M0,224L40,213.3C80,203,160,181,240,149.3C320,117,400,75,480,96C560,117,640,203,720,224C800,245,880,203,960,186.7C1040,171,1120,181,1200,186.7C1280,192,1360,192,1400,192L1440,192L1440,0L1400,0C1360,0,1280,0,1200,0C1120,0,1040,0,960,0C880,0,800,0,720,0C640,0,560,0,480,0C400,0,320,0,240,0C160,0,80,0,40,0L0,0Z"
          ></path>
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-4">
            Muscle & Posture Analysis Report
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Our advanced AI-driven assessment identifies muscle tension, balance, and posture to help you achieve an ideal body alignment and prevent injuries.
          </p>
        </motion.div>

        {/* Image Display */}
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="shadow-lg rounded-2xl overflow-hidden border border-blue-100 hover:scale-105 transition-transform duration-500"
          >
            <img src={muscleReport} alt="Muscle Report" className="w-full object-cover" />
            <div className="p-4 bg-white">
              <h3 className="text-xl font-semibold text-blue-600 mb-2">
                Muscle Report
              </h3>
              <p className="text-gray-600 text-sm">
                Visual analysis of muscle tension and relaxation zones, helping detect imbalances and areas needing therapy or strengthening.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="shadow-lg rounded-2xl overflow-hidden border border-blue-100 hover:scale-105 transition-transform duration-500"
          >
            <img src={postureReport} alt="Posture Report" className="w-full object-cover" />
            <div className="p-4 bg-white">
              <h3 className="text-xl font-semibold text-blue-600 mb-2">
                Posture Report
              </h3>
              <p className="text-gray-600 text-sm">
                AI-based posture evaluation highlighting alignment distortions and suggesting targeted corrective exercises.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-semibold text-blue-700 mb-4">
            Ready to Improve Your Posture?
          </h3>
          <p className="text-gray-600 mb-6">
            Visit our clinic to get your personalized muscle and posture analysis today.
          </p>
          <button className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold shadow-md hover:bg-blue-700 transition-transform hover:scale-105">
            Book a Consultation
          </button>
        </motion.div>
      </div>
    </section>
	
    <section className="relative bg-gradient-to-b from-blue-50 via-white to-blue-100 py-24 overflow-hidden">
      {/* SVG background */}
      <div className="absolute top-0 left-0 w-full opacity-40">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          className="w-full h-40 text-blue-200"
        >
          <path
            fill="currentColor"
            fillOpacity="1"
            d="M0,256L80,245.3C160,235,320,213,480,192C640,171,800,149,960,154.7C1120,160,1280,192,1360,208L1440,224L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
          ></path>
        </svg>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold text-blue-700 mb-4">Conditions We Treat</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">
            In our experience, most of the symptoms commonly suffered fall under these 6 categories.
            Our expertise lies in being able to get to the root cause swiftly and with utmost care.
          </p>
        </motion.div>

        {/* Body Image and Condition Cards */}
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="flex justify-center"
          >
            <img
              src={bodyImage}
              alt="Human Muscle Anatomy"
              className="max-h-[550px] w-auto drop-shadow-2xl animate-pulse"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            {conditions.map((condition, index) => (
              <motion.div
                key={index}
                whileHover={{ scale: 1.05 }}
                className="bg-white border-t-4 border-blue-500 rounded-2xl shadow-md p-5 hover:shadow-xl transition-all duration-300"
              >
                <h3 className="text-lg font-semibold text-blue-700 mb-2">
                  {condition.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {condition.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Bottom Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <h3 className="text-2xl font-semibold text-blue-700 mb-4">
            Restore Balance and Relieve Pain with Our Expert Care
          </h3>
          <button className="bg-blue-600 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:bg-blue-700 transition-transform hover:scale-105">
            Book an Appointment
          </button>
        </motion.div>
      </div>
    </section>
	<MedicalServicesSection />
	</>
  );
};

export default HomePage;
